// src/components/Admin/Contact/ContactSubmissions.tsx
import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  useTheme, 
  Tabs, 
  Tab, 
  Container, 
  Paper, 
  Card, 
  CardContent, 
  Grid,
  IconButton, 
  Tooltip, 
  alpha,
  Avatar,
  Chip,
  InputAdornment,
  TextField,
  Fade
} from "@mui/material";
import Summary from "../../../components/Admin/Common/Summary";
import CustomTable, { Column } from "../../../components/Admin/Common/Table";
import Action, { ActionItem } from "../../../components/Admin/Common/Action";
import { contactService } from "../../../services/api/contactService";
import { toast } from "react-toastify";
import { ContactSubmission } from "../../../types/contactSubmission";
import { useAuth } from "../../../contexts/AuthContext";
import ContactViewModal from "../../../components/Admin/Contact/ContactViewModal";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UpdateIcon from "@mui/icons-material/Update";
import NewMessageModal from "../../../components/Admin/Email/NewMessageModal";
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import SubjectIcon from '@mui/icons-material/Subject';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const STATUS_TABS = ["All", "New", "In Progress", "Resolved"];

const ContactSubmissions: React.FC = () => {
  const theme = useTheme();
  const { tokens } = useAuth();

  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // New state for controlling the NewMessageModal for sending email to a contact
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [initialTo, setInitialTo] = useState<string>("");
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch all contact submissions when component mounts
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        if (!tokens?.idToken) throw new Error("Token is missing");
        const data = await contactService.getContacts(tokens.idToken);
        setSubmissions(data);
      } catch (err) {
        setError("Unable to load contact submissions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [tokens]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle status update for a contact submission
  const handleUpdateStatus = async (id: string, newStatus: ContactSubmission["status"]) => {
    try {
      if (!tokens?.idToken) throw new Error("Token is missing");
      await contactService.updateContact(tokens.idToken, id, { status: newStatus });
      setSubmissions((prev) =>
        prev.map((sub) => (sub.id === id ? { ...sub, status: newStatus } : sub))
      );
      setSuccess(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status. Please try again.");
    }
  };

  // Open email modal with pre-filled recipient
  const handleSendEmail = (email: string) => {
    setInitialTo(email);
    setIsNewMessageOpen(true);
  };

  // Reset filters and refresh data
  const handleRefresh = async () => {
    setLoading(true);
    setSearchTerm("");
    setStatusFilter("All");
    try {
      if (!tokens?.idToken) throw new Error("Token is missing");
      const data = await contactService.getContacts(tokens.idToken);
      setSubmissions(data);
      setSuccess("Data refreshed successfully");
    } catch (err) {
      setError("Failed to refresh data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Filter submissions based on status and search term
  const filteredSubmissions = submissions.filter(submission => {
    // Filter by status
    const matchesStatus = statusFilter === "All" || 
      (submission.status && submission.status === statusFilter);
    
    // Filter by search term (case insensitive)
    const matchesSearch = !searchTerm || 
      submission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (submission.subject && submission.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (submission.message && submission.message.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesSearch;
  });

  // Create summary stats
  const summaryStats = {
    total: submissions.length,
    new: submissions.filter(s => s.status === "New").length,
    inProgress: submissions.filter(s => s.status === "In Progress").length,
    resolved: submissions.filter(s => s.status === "Resolved").length
  };

  // Table columns definition
  const columns: Column<ContactSubmission>[] = [
    { 
      label: "Contact", 
      key: "name",
      format: (value: string, row?: ContactSubmission) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: row?.status === "New" 
                ? alpha(theme.palette.warning.main, 0.1)
                : row?.status === "In Progress"
                  ? alpha(theme.palette.info.main, 0.1)
                  : alpha(theme.palette.success.main, 0.1),
              color: row?.status === "New" 
                ? theme.palette.warning.main
                : row?.status === "In Progress"
                  ? theme.palette.info.main
                  : theme.palette.success.main,
              mr: 2,
              width: 40, 
              height: 40
            }}
          >
            {value && value.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Box sx={{ fontWeight: 600 }}>{value || 'Unknown'}</Box>
            <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row?.email || ''}</Box>
          </Box>
        </Box>
      )
    },
    { 
      label: "Subject", 
      key: "subject",
      format: (value: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SubjectIcon sx={{ mr: 1, color: theme.palette.text.secondary, fontSize: 'small' }} />
          <Typography noWrap sx={{ maxWidth: 200 }}>
            {value || "No Subject"}
          </Typography>
        </Box>
      )
    },
    { 
      label: "Status", 
      key: "status",
      format: (value: string) => (
        <Chip
          label={value || "New"}
          size="small"
          color={
            value === "New" 
              ? "warning" 
              : value === "In Progress" 
                ? "info" 
                : "success"
          }
          icon={
            value === "New" 
              ? <MarkEmailUnreadIcon fontSize="small" /> 
              : value === "In Progress" 
                ? <UpdateIcon fontSize="small" /> 
                : <MarkEmailReadIcon fontSize="small" />
          }
          sx={{ fontWeight: 500 }}
        />
      )
    },
    { 
      label: "Date", 
      key: "createdAt",
      format: (value: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccessTimeIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 'small' }} />
          <Typography fontWeight={500}>
            {new Date(value).toLocaleDateString()}
          </Typography>
        </Box>
      )
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: theme.palette.mode === 'light' 
          ? alpha(theme.palette.primary.light, 0.04)
          : theme.palette.background.default,
        minHeight: "100vh",
        pb: 6
      }}
      aria-label="Contact Management"
    >
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0, 
          py: 3, 
          px: 4, 
          mb: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <Avatar 
                sx={{ 
                  mr: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  width: 48, 
                  height: 48 
                }}
              >
                <EmailIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <div>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                  Contact Management
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Manage and respond to contact form submissions
                </Typography>
              </div>
            </Box>

            <Box display="flex" gap={1}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  onClick={handleRefresh}
                  color="primary"
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help" arrow>
                <IconButton
                  sx={{ 
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.15),
                    }
                  }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Error and Success Messages */}
        {error && (
          <Box mb={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), borderLeft: `4px solid ${theme.palette.error.main}`, borderRadius: 1 }}>
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          </Box>
        )}
        
        {success && (
          <Fade in={!!success}>
            <Box mb={3}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), borderLeft: `4px solid ${theme.palette.success.main}`, borderRadius: 1 }}>
                <CardContent>
                  <Typography color="success.main">{success}</Typography>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        )}

        {/* Summary Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%', 
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1), 
                      color: theme.palette.primary.main,
                      mr: 2
                    }}
                  >
                    <EmailIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Total Submissions
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryStats.total.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.warning.main, 0.1), 
                      color: theme.palette.warning.main,
                      mr: 2
                    }}
                  >
                    <MarkEmailUnreadIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      New Messages
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryStats.new.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${summaryStats.total > 0 ? ((summaryStats.new / summaryStats.total) * 100).toFixed(0) : 0}% of total`} 
                  size="small" 
                  color="warning" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.info.main, 0.1), 
                      color: theme.palette.info.main,
                      mr: 2
                    }}
                  >
                    <UpdateIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      In Progress
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryStats.inProgress.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${summaryStats.total > 0 ? ((summaryStats.inProgress / summaryStats.total) * 100).toFixed(0) : 0}% of total`} 
                  size="small" 
                  color="info" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      color: theme.palette.success.main,
                      mr: 2
                    }}
                  >
                    <CheckCircleIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Resolved
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryStats.resolved.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${summaryStats.total > 0 ? ((summaryStats.resolved / summaryStats.total) * 100).toFixed(0) : 0}% of total`} 
                  size="small" 
                  color="success" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card 
          elevation={2} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            overflow: 'visible' 
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  placeholder="Search by name, email, subject, or message content..."
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { 
                      borderRadius: 2,
                      maxWidth: 400
                    }
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <FilterListIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" sx={{ mr: 2 }}>Filter by status:</Typography>
                  <Tabs
                    value={statusFilter}
                    onChange={(_, newValue) => setStatusFilter(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                      minHeight: 'unset',
                      '.MuiTab-root': {
                        minHeight: 'unset',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }
                    }}
                  >
                    {STATUS_TABS.map((status) => (
                      <Tab key={status} label={status} value={status} />
                    ))}
                  </Tabs>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table Section */}
        <Card 
          elevation={3} 
          sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            '& .MuiPaper-root': {
              boxShadow: 'none'
            }
          }}
        >
          <CustomTable<ContactSubmission>
            columns={columns}
            data={filteredSubmissions}
            loading={loading}
            rowActions={(submission) => {
              const actions: ActionItem[] = [
                {
                  label: "View Details",
                  icon: <VisibilityIcon fontSize="small" />,
                  onClick: () => setSelectedSubmission(submission),
                  color: theme.palette.info.main
                },
                {
                  label: "Send Email",
                  icon: <EmailIcon fontSize="small" />,
                  onClick: () => handleSendEmail(submission.email),
                  color: theme.palette.primary.main
                },
                (submission.status?.toLowerCase() ?? "") === "new"
                  ? {
                      label: "Mark In Progress",
                      icon: <UpdateIcon fontSize="small" />,
                      onClick: () => handleUpdateStatus(submission.id, "In Progress"),
                      color: theme.palette.info.main
                    }
                  : null,
                (submission.status?.toLowerCase() ?? "") === "in progress"
                  ? {
                      label: "Resolve",
                      icon: <CheckCircleIcon fontSize="small" />,
                      onClick: () => handleUpdateStatus(submission.id, "Resolved"),
                      color: theme.palette.success.main
                    }
                  : null,
              ].filter((action): action is Exclude<typeof action, null> => action !== null);

              return <Action actions={actions} />;
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(newRows) => {
              setRowsPerPage(newRows);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Card>
      </Container>

      {/* View Contact Modal */}
      {selectedSubmission && (
        <ContactViewModal
          open={!!selectedSubmission}
          contact={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {/* Email Modal */}
      <NewMessageModal
        open={isNewMessageOpen}
        onClose={() => setIsNewMessageOpen(false)}
        onMessageSent={(email) => {
          setSuccess("Email sent successfully.");
          setIsNewMessageOpen(false);
        }}
        initialTo={initialTo}
      />
    </Box>
  );
};

export default ContactSubmissions;
